import React, { useState, useEffect, useMemo } from 'react';
import {
    makeStyles, mergeClasses, tokens,
    Dialog, DialogSurface, DialogBody, DialogTitle, DialogContent, DialogActions,
    Button, Input, Field, Text, Tooltip,
    MessageBar, MessageBarBody, MessageBarActions,
} from '@fluentui/react-components';
import { iconMap } from '../../theme/icons';
import SelectionBox from '../components/blocks/SelectionBox';

import pic2 from '../../assets/img/icons/vidus/2.jpg';
import pic5 from '../../assets/img/icons/vidus/5.jpg';
import pic9 from '../../assets/img/icons/vidus/9.jpg';
import pic10 from '../../assets/img/icons/vidus/10.jpg';
import pic11 from '../../assets/img/icons/vidus/11.jpg';
import pic12 from '../../assets/img/icons/vidus/12.jpg';

const CloseIcon = iconMap.close;
const AddIcon = iconMap.add;
const MoveUpIcon = iconMap.move_up;
const MoveDownIcon = iconMap.move_down;
const InfoIcon = iconMap.info;
const EmptyIcon = iconMap.inventory_2;

// Visi sekcijos komponentai — kabyklas, pakabas, stalčius ir lentynas dabar
// galima laisvai derinti ir dėti kelis į tą pačią sekciją.
const ALL_OPTIONS = [
    { image: pic2,  value: 1,  label: 'Drabužių kabykla',    description: 'Viena kabykla' },
    { image: pic9,  value: 8,  label: 'Ištraukiama kabykla', description: 'Ištraukiama pakaba' },
    { image: pic10, value: 9,  label: 'Pantografas',         description: 'Nuleidžiama kabykla' },
    { image: pic5,  value: 4,  label: 'Stalčius',              description: 'Vienas didelis stalčius' },
    { image: pic11, value: 10, label: 'Lentyna',               description: 'Kintamo aukščio lentyna' },
    { image: pic12, value: 11, label: 'Lentyna su bėgeliais',  description: 'Ištraukiama lentyna' },
];

// Kabyklos/pakabos tipai. Vartotojas įveda VIENĄ bendrą aukštį tiesiai (nuo skyriaus
// viršaus/aukščiau esančio objekto iki šio komponento apačios) — jokio atskiro
// "įrenginio + tarpo" dalinimo, "item.height" = tai, ką vartotojas įvedė, tiesiogiai.
// Kiek konkrečiai kartelė/pantografas nuleidžiami žemiau šio aukščio viršaus yra grynai
// mechaninė detalė (žr. HANGER_DROP_CM), naudojama tik paaiškinimo tekste.
const HANGER_TYPES = new Set([1, 2, 8, 9]);
const HANGER_DEFAULT_HEIGHT_CM = 100;
const HANGER_MIN_HEIGHT_CM = { 1: 30, 2: 30, 8: 30, 9: 91 };
const HANGER_DROP_CM = { 1: 5, 2: 5, 8: 0, 9: 3 };

// Lentynų tipai (paprasta ir su bėgeliais). Pačios lentynos storis yra fiksuotas,
// o "aukštis virš lentynos" (vieta ištraukti/pasiekti) yra redaguojamas item.topGap
// laukas — veikia bet kurioje pozicijoje, ne tik kaip pirmam sekcijos komponentui.
const SHELF_TYPES = new Set([10, 11]);
const SHELF_THICKNESS_CM = { 10: 1.8, 11: 6 };
const SHELF_TOP_GAP_DEFAULT_CM = { 10: 27, 11: 27 };
const SHELF_MIN_TOP_GAP_CM = { 10: 8, 11: 2.5 };

const DEFAULT_HEIGHTS = { 4: 20 };
const MIN_DRAWER_HEIGHT_CM = 8;
// Elemento sąrašo pavadinimas ir ikona — imami tiesiai iš ALL_OPTIONS (tas pats
// paveiksliukas kaip "Pridėti komponentą" pasirinkime, tik mažesnis sąraše), kad
// niekada neišsiskirtų nuo pasirinkimo dialogo. Tipas 2 (sena, nebepasirenkama
// "Dviguba kabykla") paliktas kaip atskiras užpildas jau išsaugotoms konfigūracijoms.
export const ITEM_LABELS = { ...Object.fromEntries(ALL_OPTIONS.map(o => [o.value, o.label])), 2: 'Dviguba kabykla' };
export const ITEM_IMAGES = { ...Object.fromEntries(ALL_OPTIONS.map(o => [o.value, o.image])), 2: pic2 };

// Paaiškinimai, rodomi užvedus pelę ant komponento matmens įvesties lauko.
const FIELD_TOOLTIPS = {
    1: `Nurodykite bendrą kabyklos aukštį iki viršutinės lentynos ar kito objekto viršaus.
Kartelė automatiškai nuleidžiama žemiau (${HANGER_DROP_CM[1].toFixed(1)} cm).
Minimalus kabyklos aukštis: ${HANGER_MIN_HEIGHT_CM[1].toFixed(1)} cm.
Rekomenduojamas aukštis: ${HANGER_DEFAULT_HEIGHT_CM.toFixed(1)} cm.`,
    8: `Nurodykite bendrą kabyklos aukštį iki viršutinės lentynos.
Ji tvirtinama tiesiai prie viršutinės lentynos.
Minimalus kabyklos aukštis: ${HANGER_MIN_HEIGHT_CM[8].toFixed(1)} cm.
Rekomenduojamas aukštis: ${HANGER_DEFAULT_HEIGHT_CM.toFixed(1)} cm.`,
    9: `Nurodykite bendrą kabyklos aukštį iki viršutinės lentynos.
Pantografas kabo nuleistas ${HANGER_DROP_CM[9].toFixed(1)} cm nuo viršaus (paties pantografo aukštis 88.0 cm).
Minimalus kabyklos aukštis: ${HANGER_MIN_HEIGHT_CM[9].toFixed(1)} cm.
Rekomenduojamas aukštis: ${HANGER_DEFAULT_HEIGHT_CM.toFixed(1)} cm.`,
    11: `Nurodykite skyriaus aukštį virš ištraukiamos lentynos.
Lentynos storis (6.0cm) pridedamas automatiškai.
Minimalus ištraukiamos lentynos aukštis 2.5 cm.
Rekomenduojamas aukštis: 27.0 cm.`,
    10: `Nurodykite skyriaus aukštį virš keičiamo aukščio lentynos.
Lentynos storis (1.8cm) pridedamas automatiškai.
Minimalus ištraukiamos lentynos aukštis 8.0 cm.
Rekomenduojamas aukštis: 27.0 cm.`,
};
const drawerTooltip = (maxCm) => `Nurodykite stalčiaus fasado aukštį.
Tarpelis (${(DRAWER_GAP * 100).toFixed(1)}cm) pirštams pridedamas automatiškai.
Minimalus stalčiaus aukštis ${MIN_DRAWER_HEIGHT_CM.toFixed(1)} cm.
Maksimalus stalčiaus aukštis ${maxCm.toFixed(1)}cm.
Rekomenduojamas aukštis: ${DEFAULT_HEIGHTS[4].toFixed(1)} cm.`;

// 2.5cm gap virš kiekvieno komponento (tarp jo viršaus ir aukščiau esančio elemento/lentynos)
const DRAWER_GAP = 0.025;

// Bet koks skaičius, gautas iš metrų*100 aritmetikos (pvz. sH*100), gali turėti slankaus
// kablelio paklaidą (114.999999... vietoj 115) — apvaliname iki 1 skaičiaus po kablelio
// visur, kur toks skaičius patenka į redaguojamą lauką arba išsaugomus duomenis.
const roundCm = (n) => Math.round(n * 10) / 10;

// Visi skaičių laukai (viršaus/apačios tarpai, komponentų aukščiai) turi vienodą
// plotį ir naudoja tą patį Fluent Input išvaizdą visame dialoge — vienas švarus
// rėmelis aplink visą lauką (skaičius + "cm"), be papildomų perrašymų.
const NUMBER_FIELD_WIDTH = 116;
const cmAdornment = <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>cm</Text>;
// Priverčia paaiškinimo debesėlį visada likti virš lauko — be šito Fluent pozicionavimas
// kartais jį "apverčia" žemyn (pvz. kai lauko apačioje trūksta laisvos vietos scroll'inamame
// dialog turinyje), o tada jis atrodo prilipęs prie dialog lango apačios.
const TOOLTIP_POSITIONING = { position: 'above', fallbackPositions: [] };
// Etiketės plotis prieš komponento laukus — fiksuotas, kad patys laukai visose
// eilutėse prasidėtų (ir sulygiuotų) toje pačioje vietoje kairėje pusėje.
const ITEM_LABEL_WIDTH = 168;

const useStyles = makeStyles({
    surface: { maxWidth: '37.5rem', width: '100%' },
    body: { minHeight: 0 },
    dialogTitle: { borderBottom: `1px solid ${tokens.colorNeutralStroke2}` },
    titleAction: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS },
    heightBadge: {
        display: 'inline-flex', alignItems: 'center',
        backgroundColor: tokens.colorNeutralBackground3, borderRadius: tokens.borderRadiusCircular,
        paddingLeft: tokens.spacingHorizontalM, paddingRight: tokens.spacingHorizontalM,
        paddingTop: tokens.spacingVerticalXXS, paddingBottom: tokens.spacingVerticalXXS,
        fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground1, whiteSpace: 'nowrap',
    },
    heightBadgeValue: { fontWeight: tokens.fontWeightSemibold },
    content: {
        paddingTop: '10px', display: 'flex', flexDirection: 'column',
        minHeight: 0, maxHeight: '75vh', overflowY: 'auto',
    },
    emptyState: { textAlign: 'center', paddingTop: '32px', paddingBottom: '32px', color: tokens.colorNeutralForeground3 },
    emptyStateIcon: { fontSize: '32px', opacity: 0.5 },
    emptyStateTitle: { marginTop: '4px' },
    emptyStateSubtitle: { fontSize: tokens.fontSizeBase200 },
    contentStack: { display: 'flex', flexDirection: 'column', gap: '10px' },
    itemCard: {
        border: `1px solid ${tokens.colorNeutralStroke2}`, borderRadius: tokens.borderRadiusLarge,
        backgroundColor: tokens.colorNeutralBackground1, overflow: 'hidden',
    },
    itemRow: {
        display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS,
        paddingLeft: tokens.spacingHorizontalM, paddingRight: tokens.spacingHorizontalM,
        paddingTop: tokens.spacingVerticalS, paddingBottom: tokens.spacingVerticalS,
    },
    moveStack: { display: 'flex', flexDirection: 'column', marginRight: tokens.spacingHorizontalXXS },
    moveBtn: { minWidth: '20px', width: '20px', height: '20px', padding: 0, fontSize: '16px' },
    itemThumb: { width: '24px', height: '24px', borderRadius: tokens.borderRadiusMedium, objectFit: 'cover', flexShrink: 0 },
    itemLabel: { width: `${ITEM_LABEL_WIDTH}px`, flexShrink: 0, fontSize: tokens.fontSizeBase300, fontWeight: tokens.fontWeightMedium },
    spacer: { flex: 1 },
    removeBtn: {
        minWidth: 'auto', padding: tokens.spacingHorizontalXS, fontSize: '16px', color: tokens.colorNeutralForegroundDisabled,
        ':hover': { color: tokens.colorPaletteRedForeground1 },
    },
    itemNote: {
        display: 'block', fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3,
        paddingLeft: '64px', paddingRight: tokens.spacingHorizontalM, paddingBottom: tokens.spacingVerticalS,
    },
    // borderStyle (be krypties) yra Griffel UNSUPPORTED_CSS_PROPERTIES sąraše —
    // tyliai išmetama (žr. SelectionBox.jsx komentarą), tad reikia visų 4
    // kryptinių borderXStyle vietoj to, kad brūkšninis rėmelis realiai rodytųsi.
    addButton: {
        width: '100%',
        borderTopStyle: 'dashed', borderRightStyle: 'dashed', borderBottomStyle: 'dashed', borderLeftStyle: 'dashed',
        paddingTop: tokens.spacingVerticalS, paddingBottom: tokens.spacingVerticalS,
    },
    pickingBox: { position: 'relative', padding: tokens.spacingHorizontalM, borderRadius: tokens.borderRadiusLarge, backgroundColor: tokens.colorNeutralBackground1 },
    pickingCloseBtn: {
        position: 'absolute', top: tokens.spacingVerticalSNudge, right: tokens.spacingHorizontalSNudge,
        minWidth: 'auto', padding: tokens.spacingHorizontalXS, fontSize: '16px',
        color: tokens.colorNeutralForegroundDisabled, ':hover': { color: tokens.colorPaletteRedForeground1 },
    },
    pickingGrid: { display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-start', gap: tokens.spacingHorizontalM, paddingTop: tokens.spacingVerticalXXL },
    gapRow: {
        display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalM,
        paddingLeft: tokens.spacingHorizontalM, paddingRight: tokens.spacingHorizontalM,
        paddingTop: tokens.spacingVerticalS, paddingBottom: tokens.spacingVerticalS,
        borderRadius: tokens.borderRadiusLarge, border: `1px dashed ${tokens.colorNeutralStroke2}`,
    },
    gapRowDisabled: { backgroundColor: tokens.colorNeutralBackground3 },
    gapRowLabel: { fontWeight: tokens.fontWeightMedium, color: tokens.colorNeutralForeground1 },
    gapRowLabelDisabled: { color: tokens.colorNeutralForeground3 },
    numberInput: { width: `${NUMBER_FIELD_WIDTH}px` },
    numberInputField: { textAlign: 'center' },
    heightFieldTooltipWrap: { display: 'inline-flex', alignItems: 'center', gap: tokens.spacingHorizontalXS },
    infoIcon: { fontSize: '16px', color: tokens.colorNeutralForegroundDisabled, cursor: 'help', flexShrink: 0 },
    tooltipContent: { whiteSpace: 'pre-line', fontSize: tokens.fontSizeBase200 },
    actions: {
        paddingLeft: tokens.spacingHorizontalXXL, paddingRight: tokens.spacingHorizontalXXL,
        paddingTop: tokens.spacingVerticalL, paddingBottom: tokens.spacingVerticalL,
        gap: tokens.spacingHorizontalS, borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    },
    actionBtn: { flex: 1 },
});

// Kompaktiška riboto dydžio eilutė ("Laisvas aukštis viršuje/apačioje").
const GapRow = ({ label, value, onChange, disabled }) => {
    const s = useStyles();
    return (
        <div className={mergeClasses(s.gapRow, disabled && s.gapRowDisabled)}>
            <Text className={mergeClasses(s.gapRowLabel, disabled && s.gapRowLabelDisabled)}>
                {label}
            </Text>
            <Input
                value={value}
                onChange={onChange ? (_e, data) => onChange(data.value) : undefined}
                size="small"
                type="number"
                disabled={disabled}
                min={0}
                step={0.5}
                contentAfter={cmAdornment}
                input={{ className: mergeClasses('fitit-inner-number-input', s.numberInputField) }}
                className={s.numberInput}
            />
        </div>
    );
};

// Vieno komponento "Aukštis" laukas — vienoda vizuali forma visiems tipams,
// redaguojama arba fiksuota/neaktyvi, visada su "cm" ženkliuku vietoj teksto etiketėje.
// Jei paduotas `tooltip`, jis rodomas užvedus pelę ant lauko.
//
// Redagavimo metu naudojama LOKALI būsena (localVal), o `onChange` iškviečiamas tik
// kai laukas prarandamas (blur) arba paspaudus Enter — ne kiekvienu klavišo paspaudimu.
// Anksčiau `value` buvo tiesiogiai susietas su tėvinio komponento reikšme, kuri iškart
// nuvalidavus (pvz. min patikra) grąžindavo lauką prie SENOS reikšmės, jei tarpinė
// įvedimo būsena (pvz. "3" prieš įvedant "30") būdavo mažesnė už minimumą — dėl to
// atrodydavo, kad įmanoma įvesti tik vieną skaičių iš karto. `onFocus` pažymi visą
// tekstą, kad naują reikšmę būtų galima iškart perrašyti (kaip ir kituose skaitiniuose
// laukuose per programą, pvz. ShelfInput.jsx).
const HeightField = ({ value, onChange, disabled, min, helper, tooltip }) => {
    const s = useStyles();
    const [localVal, setLocalVal] = useState(null);
    const displayVal = localVal !== null ? localVal : value;

    const commit = () => {
        if (localVal !== null) {
            onChange({ target: { value: localVal } });
            setLocalVal(null);
        }
    };

    const field = (
        <Field hint={helper}>
            <Input
                value={String(displayVal)}
                onChange={(_e, data) => setLocalVal(data.value)}
                onFocus={(e) => e.target.select()}
                onBlur={commit}
                onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                size="small"
                type="number"
                disabled={disabled}
                min={min}
                step={1}
                contentAfter={cmAdornment}
                input={{ className: mergeClasses('fitit-inner-number-input', s.numberInputField) }}
                className={s.numberInput}
            />
        </Field>
    );
    if (!tooltip) return field;
    return (
        <Tooltip
            content={<span className={s.tooltipContent}>{tooltip}</span>}
            withArrow
            relationship="description"
            positioning={TOOLTIP_POSITIONING}
        >
            <div className={s.heightFieldTooltipWrap}>
                {field}
                <InfoIcon className={s.infoIcon} />
            </div>
        </Tooltip>
    );
};

const makeHangerItem = (type, height = HANGER_DEFAULT_HEIGHT_CM) => ({ type, height });
const makeShelfItem = (type, topGap) => ({ type, topGap, height: SHELF_THICKNESS_CM[type] });

// existingItemsCount = kiek komponentų sekcijoje jau yra prieš pridedant naują.
// lastItemType = tipas komponento, kuris šiuo metu yra paskutinis (žemiausias) sąraše —
// būtent jis atsidurs virš naujai pridedamo elemento.
const isInnerOptionDisabled = (value, dims, existingItemsCount = 0, lastItemType = null) => {
    // Pantografas ir ištraukiama kabykla — abu tvirtinami tiesiai prie sekcijos
    // viršuje esančios lentynos, todėl nelogiška juos dėti po jau esančiais
    // komponentais (stalčiais ir pan.). Leidžiama pridėti tik kaip pirmą
    // (vienintelį iki tol) sekcijos komponentą.
    if ((value === 8 || value === 9) && existingItemsCount > 0) return true;
    // Stalčius — leidžiama pridėti tik jei virš jo bus sekcijos karkaso plokštė
    // (sekcija dar tuščia), kitas stalčius arba lentyna su bėgeliais. Po kabykla ar
    // kintamo aukščio lentyna stalčiaus dėti nelogiška.
    if (value === 4 && existingItemsCount > 0 && lastItemType !== 4 && lastItemType !== 11) return true;
    if (!dims) return false;
    const { widthCm: w, heightCm: h, depthCm: d } = dims;
    switch (value) {
        case 1: case 8: return h < 50;
        case 2: return h < 90;
        case 4: return false;
        case 9: return h < 100 || w < 54.5 || w > 120;
        case 10: return w < 10 || w > 90 || d < 20;
        case 11: return w < 20 || w > 90 || d < 25;
        default: return false;
    }
};

const InnerSelectingDialog = ({ open, onClose, selectedSection, sectionOptions, onOptionSelect, sectionDims }) => {
    const s = useStyles();
    const key = selectedSection?.furnitureId && selectedSection?.cabinetIndex !== null && selectedSection?.shelfIndex !== null
        ? `${selectedSection.furnitureId}-${selectedSection.cabinetIndex}-${selectedSection.shelfIndex}`
        : null;
    const currentValue = key ? (sectionOptions[key] ?? 0) : 0;
    const sH = (sectionDims?.heightCm || 9999) / 100;

    const [items, setItems] = useState([]);
    const [topGapCm, setTopGapCm] = useState(0);
    const [picking, setPicking] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');

    useEffect(() => {
        if (!open) return;
        if (Array.isArray(currentValue)) {
            // Backfill fields for items saved before they existed.
            let normalized = currentValue.map(it => {
                if (SHELF_TYPES.has(it.type) && it.topGap === undefined) {
                    const thickness = SHELF_THICKNESS_CM[it.type];
                    const topGap = Math.max(SHELF_MIN_TOP_GAP_CM[it.type], (it.height || thickness) - thickness);
                    return { ...it, topGap, height: thickness };
                }
                return it;
            });
            // Derive the actual top gap from the first item's real position rather than
            // trusting the separately-stored topGapCm, which goes stale whenever the item
            // is repositioned directly in the 3D view (e.g. dragged) without going through
            // this dialog — posY is always the source of truth for what's really rendered.
            // Not meaningful when the first item is a shelf type — those use their own
            // per-item topGap regardless of position instead.
            const first = normalized[0];
            let effectiveTopGapCm;
            if (first && SHELF_TYPES.has(first.type)) {
                effectiveTopGapCm = 0;
            } else {
                const derivedTopGapCm = first?.posY !== undefined
                    ? Math.max(0, sH * 100 - (first.posY * 100 + (first.height || 30)))
                    : (first?.topGapCm ?? 0);
                effectiveTopGapCm = Math.round(derivedTopGapCm * 10) / 10;
            }
            setTopGapCm(effectiveTopGapCm);

            // A trailing hanger (nothing below it) always fills 100% of whatever space is
            // actually left in the section — grows or shrinks together with the shelf above
            // it, down to its own minimum — instead of holding onto whatever height was
            // typed in even after a neighboring resize leaves extra/insufficient room.
            const lastIdx = normalized.length - 1;
            const lastItem = normalized[lastIdx];
            if (lastItem && HANGER_TYPES.has(lastItem.type)) {
                let topEdgeCm = sH * 100;
                for (let idx = 0; idx < lastIdx; idx++) {
                    const it = normalized[idx];
                    const gapAbove = SHELF_TYPES.has(it.type)
                        ? (it.topGap ?? SHELF_TOP_GAP_DEFAULT_CM[it.type])
                        : (idx === 0 ? effectiveTopGapCm : DRAWER_GAP * 100);
                    topEdgeCm -= gapAbove + (it.height || 30);
                }
                const lastGapAbove = SHELF_TYPES.has(lastItem.type)
                    ? (lastItem.topGap ?? SHELF_TOP_GAP_DEFAULT_CM[lastItem.type])
                    : (lastIdx === 0 ? effectiveTopGapCm : DRAWER_GAP * 100);
                const minH = HANGER_MIN_HEIGHT_CM[lastItem.type] ?? 30;
                const filledHeight = roundCm(Math.max(minH, topEdgeCm - lastGapAbove));
                normalized = normalized.map((it, idx) => idx === lastIdx ? { ...it, height: filledHeight } : it);
            }

            setItems(normalized);
        } else if (currentValue) {
            // Sena konfigūracija, kur sekcija turėjo vieną kabyklos/pakabos reikšmę
            // (ne masyvą) — perkeliame į naują sąrašo formatą.
            const item = HANGER_TYPES.has(currentValue)
                ? makeHangerItem(currentValue)
                : { type: currentValue, height: DEFAULT_HEIGHTS[currentValue] || 30 };
            setItems([item]);
            setTopGapCm(0);
        } else {
            setItems([]);
            setTopGapCm(0);
        }
        setPicking(false);
        setErrorMsg('');
    }, [open]);

    // Clear error whenever items list changes
    useEffect(() => { setErrorMsg(''); }, [items]);

    // Gap ABOVE this item: shelf types always use their own topGap (regardless of
    // position); everything else uses the position-based rule (topGapCm for the very
    // first item, fixed DRAWER_GAP between any other pair of items). A drawer's gap
    // above it is never optional — even as the first item, it can never go below the
    // same fixed DRAWER_GAP every other drawer gets, so the finger clearance to
    // whatever's above (shelf or section ceiling) is always visibly there in 3D.
    const getGapAboveCm = (item, idx) => {
        if (SHELF_TYPES.has(item.type)) return item.topGap ?? SHELF_TOP_GAP_DEFAULT_CM[item.type];
        if (idx !== 0) return DRAWER_GAP * 100;
        return item.type === 4 ? Math.max(DRAWER_GAP * 100, topGapCm) : topGapCm;
    };

    const handleConfirm = () => {
        if (items.length === 0) {
            onOptionSelect(0);
            onClose();
            return;
        }

        // Validate each item
        for (const item of items) {
            const h = (item.height || 30) / 100;
            if (HANGER_TYPES.has(item.type)) {
                const minH = HANGER_MIN_HEIGHT_CM[item.type] ?? 30;
                if ((item.height || 0) < minH) {
                    setErrorMsg(`${ITEM_LABELS[item.type] || 'Elementas'}: aukštis negali būti mažesnis nei ${minH} cm (dabartinis: ${item.height} cm).`);
                    return;
                }
            } else if (SHELF_TYPES.has(item.type)) {
                const topGap = item.topGap ?? SHELF_TOP_GAP_DEFAULT_CM[item.type];
                const minGap = SHELF_MIN_TOP_GAP_CM[item.type];
                if (topGap < minGap) {
                    setErrorMsg(`${ITEM_LABELS[item.type] || 'Elementas'}: aukštis virš lentynos negali būti mažesnis nei ${minGap} cm (dabartinis: ${topGap} cm).`);
                    return;
                }
            } else if (item.type === 4 && item.height < MIN_DRAWER_HEIGHT_CM) {
                setErrorMsg(`Stalčiaus aukštis negali būti mažesnis nei ${MIN_DRAWER_HEIGHT_CM} cm (dabartinis: ${item.height} cm).`);
                return;
            }
            if (h > sH + 0.001) {
                setErrorMsg(
                    `${ITEM_LABELS[item.type] || 'Elementas'} per aukštas: ${item.height} cm` +
                    (sectionDims?.heightCm ? ` (sekcija: ${sectionDims.heightCm} cm)` : '')
                );
                return;
            }
        }

        // Total space actually needed (every item's own gap-above + height).
        // restack() clamps posY at 0 instead of going negative, so overflow has to be
        // detected here first, before it silently produces overlapping items.
        const totalNeededM = items.reduce((sum, it, idx) => sum + getGapAboveCm(it, idx) / 100 + (it.height || 30) / 100, 0);
        if (totalNeededM > sH + 0.001) {
            setErrorMsg(
                `Komponentai netelpa į sekciją (reikia ${(totalNeededM * 100).toFixed(1)} cm, yra ${(sH * 100).toFixed(1)} cm). Sumažinkite tarpus ar aukščius.`
            );
            return;
        }

        // Always recompute every item's position top-to-bottom from the current
        // topGapCm/heights/gaps — this dialog is the authoritative editor for the
        // section's layout, so changing a number here must visibly move things in
        // the 3D view, not silently keep stale posY values from a previous save.
        const finalItems = restack(items);

        if (finalItems.length === 0) {
            onOptionSelect(0);
        } else {
            const savedTopGapCm = items[0]?.type === 4 ? Math.max(DRAWER_GAP * 100, topGapCm) : topGapCm;
            onOptionSelect([{ ...finalItems[0], topGapCm: savedTopGapCm }, ...finalItems.slice(1)]);
        }
        onClose();
    };

    // Kiek vietos (cm) liktų naujam, dabar pridedamam stalčiui, jei jis būtų dedamas su
    // standartiniu tarpu virš jo — naudojama, kad standartinio dydžio netelpantis
    // komponentas būtų automatiškai sutalpintas pagal likusią vietą, o ne apskritai
    // neįdedamas.
    const getNewItemBudgetCm = (type) => {
        if (items.length === 0) {
            const gapAbove = type === 4 ? Math.max(DRAWER_GAP * 100, topGapCm) : topGapCm;
            return roundCm(Math.max(0, sH * 100 - gapAbove));
        }
        return roundCm(Math.max(0, freeBottomCm - DRAWER_GAP * 100));
    };

    const handleAddItem = (type) => {
        let item;
        if (HANGER_TYPES.has(type)) {
            // Pridedama kabykla visada tampa paskutiniu sekcijos komponentu, todėl jai
            // iškart atiduodama VISA likusi vieta (ne fiksuotas 100cm numatytasis) — kad
            // po kabykla neliktų nepanaudoto "Laisvo aukščio apačioje". Vartotojas vis
            // tiek gali šį lauką rankiniu būdu sumažinti.
            const minH = HANGER_MIN_HEIGHT_CM[type] ?? 30;
            const budget = getNewItemBudgetCm(type);
            const height = Math.max(minH, budget);
            item = makeHangerItem(type, height);
        } else if (SHELF_TYPES.has(type)) {
            // Jei standartinis 27cm tarpas virš lentynos netelpa į likusią vietą,
            // sumažiname jį iki to, kas tilpsta (bet ne mažiau nei minimumo) —
            // veikia vienodai nepriklausomai nuo pozicijos sekcijoje.
            const available = items.length === 0 ? roundCm(sH * 100) : freeBottomCm;
            const budget = roundCm(Math.max(0, available - SHELF_THICKNESS_CM[type]));
            const topGap = Math.min(SHELF_TOP_GAP_DEFAULT_CM[type], Math.max(SHELF_MIN_TOP_GAP_CM[type], budget));
            item = makeShelfItem(type, topGap);
        } else if (type === 4) {
            // Jei standartinis 20cm stalčius netelpa į likusią vietą, sumažiname jo
            // aukštį iki to, kas realiai tilpsta (bet ne mažiau nei MIN_DRAWER_HEIGHT_CM).
            const budget = getNewItemBudgetCm(4);
            const height = Math.min(DEFAULT_HEIGHTS[4], Math.max(MIN_DRAWER_HEIGHT_CM, budget));
            item = { type, height };
        } else {
            item = { type, height: DEFAULT_HEIGHTS[type] || 30 };
        }

        setItems(prev => [...prev, item]);
        setPicking(false);
    };

    const handleRemoveItem = (index) => {
        const removed = items[index];
        if (!removed) return;
        // Preserve the REMAINING items' absolute positions: fold the removed item's own
        // vacated space (its gap-above + its height) into whatever now takes its old
        // slot, instead of letting the whole stack below it silently slide up to close
        // the gap (see /admin/bugs "lentynos pakeite pozicija, turejo likti savo senoje
        // pozicijoje"). Only shelf types (own `topGap` field) and a new first item (via
        // topGapCm) have an adjustable gap to grow — a non-shelf item staying in a
        // middle slot always uses the fixed DRAWER_GAP above it and has nothing to
        // compensate with, so it keeps closing up (unchanged, pre-existing behavior); a
        // trailing hanger is meant to auto-fill whatever space is left below it anyway.
        const removedSpaceCm = getGapAboveCm(removed, index) + (removed.height || 30);
        const rest = items.filter((_, i) => i !== index);
        const next = rest[index];
        if (next) {
            if (SHELF_TYPES.has(next.type)) {
                rest[index] = { ...next, topGap: (next.topGap ?? SHELF_TOP_GAP_DEFAULT_CM[next.type]) + removedSpaceCm };
            } else if (index === 0) {
                setTopGapCm(g => g + removedSpaceCm);
            }
        }
        setItems(rest);
    };

    // Re-stack posY so dialog order = 3D order: each item's gap above it comes from
    // getGapAboveCm (shelf types use their own topGap; everything else uses topGapCm
    // for the first item and the fixed DRAWER_GAP between any other pair).
    const restack = (arr) => {
        const result = new Array(arr.length);
        let topEdge = sH;
        for (let i = 0; i < arr.length; i++) {
            const gapAbove = getGapAboveCm(arr[i], i) / 100;
            const itemH = (arr[i].height || 30) / 100;
            const posY = Math.max(0, topEdge - gapAbove - itemH);
            result[i] = { ...arr[i], posY };
            topEdge = posY;
        }
        return result;
    };

    const handleMoveUp = (index) => {
        if (index === 0) return;
        setItems(prev => {
            const arr = [...prev];
            [arr[index - 1], arr[index]] = [arr[index], arr[index - 1]];
            return restack(arr);
        });
    };

    const handleMoveDown = (index) => {
        setItems(prev => {
            if (index >= prev.length - 1) return prev;
            const arr = [...prev];
            [arr[index], arr[index + 1]] = [arr[index + 1], arr[index]];
            return restack(arr);
        });
    };

    const handleHeightChange = (index, val) => {
        const num = parseFloat(val);
        setItems(prev => prev.map((item, i) => {
            if (i !== index) return item;
            const height = isNaN(num) || num < MIN_DRAWER_HEIGHT_CM ? (item.height || DEFAULT_HEIGHTS[item.type] || MIN_DRAWER_HEIGHT_CM) : num;
            return { ...item, height };
        }));
    };

    const handleHangerHeightChange = (index, val) => {
        const num = parseFloat(val);
        setItems(prev => prev.map((item, i) => {
            if (i !== index) return item;
            const minH = HANGER_MIN_HEIGHT_CM[item.type] ?? 30;
            const height = isNaN(num) || num < minH ? item.height : num;
            return { ...item, height };
        }));
    };

    const handleShelfGapChange = (index, val) => {
        const num = parseFloat(val);
        setItems(prev => prev.map((item, i) => {
            if (i !== index) return item;
            const minGap = SHELF_MIN_TOP_GAP_CM[item.type];
            const topGap = isNaN(num) || num < minGap ? (item.topGap ?? SHELF_TOP_GAP_DEFAULT_CM[item.type]) : num;
            return { ...item, topGap };
        }));
    };

    // Free space left below the stacked items (section height minus every item's own
    // gap-above plus height), matching restack().
    const freeBottomCm = useMemo(() => {
        const usedM = items.reduce((sum, it, idx) => sum + getGapAboveCm(it, idx) / 100 + (it.height || 30) / 100, 0);
        return roundCm(Math.max(0, sH - usedM) * 100);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [items, sH, topGapCm]);

    return (
        <Dialog open={open} onOpenChange={(_e, data) => { if (!data.open) onClose(); }}>
            <DialogSurface id="dialog-inner-selecting" className={s.surface}>
                <DialogBody className={s.body}>
                    <DialogTitle
                        className={s.dialogTitle}
                        action={(
                            <div className={s.titleAction}>
                                {sectionDims?.heightCm != null && (
                                    <span className={s.heightBadge}>
                                        Skyriaus aukštis{' '}
                                        <span className={s.heightBadgeValue}>{sectionDims.heightCm.toFixed(1)} cm</span>
                                    </span>
                                )}
                                <Button appearance="subtle" icon={<CloseIcon />} aria-label="Uždaryti" onClick={onClose} />
                            </div>
                        )}
                    >
                        Pasirinkite vidinius komponentus
                    </DialogTitle>

                    <DialogContent className={s.content}>
                        {items.length === 0 && !picking && (
                            <div className={s.emptyState}>
                                <EmptyIcon className={s.emptyStateIcon} />
                                <Text className={s.emptyStateTitle} block>Sekcija tuščia</Text>
                                <Text className={s.emptyStateSubtitle} block>Pridėkite pirmą komponentą žemiau</Text>
                            </div>
                        )}

                        <div className={s.contentStack}>
                            {items.map((item, i) => {
                                const isHanger = HANGER_TYPES.has(item.type);
                                const isShelf = SHELF_TYPES.has(item.type);
                                const isLast = i === items.length - 1;

                                const note = item.type === 4
                                    ? `Tarpelis (${(DRAWER_GAP * 100).toFixed(1)}cm) pirštams virš stalčiaus pridedamas automatiškai.`
                                    : SHELF_TYPES.has(item.type)
                                        ? `Lentynos storis (${SHELF_THICKNESS_CM[item.type].toFixed(1)}cm) pridedamas automatiškai.`
                                        : null;

                                return (
                                    <div key={i} className={s.itemCard}>
                                        <div className={s.itemRow}>
                                            <div className={s.moveStack}>
                                                <Button
                                                    appearance="subtle" size="small" icon={<MoveUpIcon />}
                                                    onClick={() => handleMoveUp(i)} disabled={i === 0}
                                                    className={s.moveBtn}
                                                />
                                                <Button
                                                    appearance="subtle" size="small" icon={<MoveDownIcon />}
                                                    onClick={() => handleMoveDown(i)} disabled={isLast}
                                                    className={s.moveBtn}
                                                />
                                            </div>

                                            {ITEM_IMAGES[item.type] && (
                                                <img src={ITEM_IMAGES[item.type]} alt="" className={s.itemThumb} />
                                            )}

                                            <Text className={s.itemLabel}>
                                                {ITEM_LABELS[item.type] || `Tipas ${item.type}`}
                                            </Text>

                                            {item.type === 4 && (
                                                <HeightField
                                                    value={item.height}
                                                    onChange={(e) => handleHeightChange(i, e.target.value)}
                                                    min={MIN_DRAWER_HEIGHT_CM}
                                                    helper={`min ${MIN_DRAWER_HEIGHT_CM} / max ${Math.floor(sectionDims?.heightCm ?? 999)}`}
                                                    tooltip={drawerTooltip(Math.max(MIN_DRAWER_HEIGHT_CM, (sectionDims?.heightCm ?? 999) - getGapAboveCm(item, i)))}
                                                />
                                            )}
                                            {isHanger && (
                                                <HeightField
                                                    value={item.height}
                                                    onChange={(e) => handleHangerHeightChange(i, e.target.value)}
                                                    min={HANGER_MIN_HEIGHT_CM[item.type] ?? 30}
                                                    helper={`min ${HANGER_MIN_HEIGHT_CM[item.type] ?? 30}`}
                                                    tooltip={FIELD_TOOLTIPS[item.type]}
                                                />
                                            )}
                                            {isShelf && (
                                                <HeightField
                                                    value={item.topGap ?? SHELF_TOP_GAP_DEFAULT_CM[item.type]}
                                                    onChange={(e) => handleShelfGapChange(i, e.target.value)}
                                                    min={SHELF_MIN_TOP_GAP_CM[item.type]}
                                                    helper={`min ${SHELF_MIN_TOP_GAP_CM[item.type]}`}
                                                    tooltip={FIELD_TOOLTIPS[item.type]}
                                                />
                                            )}

                                            <div className={s.spacer} />

                                            <Button
                                                appearance="subtle" size="small" icon={<CloseIcon />}
                                                onClick={() => handleRemoveItem(i)}
                                                className={s.removeBtn}
                                            />
                                        </div>

                                        {note && (
                                            <Text className={s.itemNote} block>
                                                {note}
                                            </Text>
                                        )}
                                    </div>
                                );
                            })}

                            {errorMsg && (
                                <MessageBar intent="error">
                                    <MessageBarBody>{errorMsg}</MessageBarBody>
                                    <MessageBarActions
                                        containerAction={(
                                            <Button appearance="transparent" icon={<CloseIcon />} aria-label="Uždaryti" onClick={() => setErrorMsg('')} />
                                        )}
                                    />
                                </MessageBar>
                            )}

                            {!picking ? (
                                <Button
                                    icon={<AddIcon />}
                                    appearance="outline"
                                    onClick={() => setPicking(true)}
                                    className={s.addButton}
                                >
                                    Pridėti komponentą
                                </Button>
                            ) : (
                                <div className={s.pickingBox}>
                                    <Button
                                        appearance="subtle" size="small" icon={<CloseIcon />}
                                        onClick={() => setPicking(false)}
                                        className={s.pickingCloseBtn}
                                    />
                                    <div className={s.pickingGrid}>
                                        {ALL_OPTIONS.map(opt => {
                                            const lastItemType = items.length > 0 ? items[items.length - 1].type : null;
                                            const blockedAsNonFirst = (opt.value === 8 || opt.value === 9) && items.length > 0;
                                            const blockedDrawerPlacement = opt.value === 4 && items.length > 0 && lastItemType !== 4 && lastItemType !== 11;
                                            let subTitle = opt.description;
                                            if (blockedAsNonFirst) subTitle = 'Galimas tik kaip pirmas komponentas';
                                            else if (blockedDrawerPlacement) subTitle = 'Galimas tik virš stalčiaus arba lentynos su bėgeliais';
                                            return (
                                                <SelectionBox
                                                    key={opt.value}
                                                    image={opt.image}
                                                    title={opt.label}
                                                    subTitle={subTitle}
                                                    selected={false}
                                                    onClick={() => handleAddItem(opt.value)}
                                                    disabled={isInnerOptionDisabled(opt.value, sectionDims, items.length, lastItemType)}
                                                />
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {items.length > 0 && !HANGER_TYPES.has(items[items.length - 1].type) && (
                                <GapRow label="Laisvas aukštis apačioje" value={freeBottomCm.toFixed(1)} disabled />
                            )}
                        </div>
                    </DialogContent>

                    <DialogActions className={s.actions}>
                        <Button size="large" appearance="outline" onClick={onClose} className={s.actionBtn}>Atšaukti</Button>
                        <Button size="large" appearance="primary" onClick={handleConfirm} className={s.actionBtn}>Išsaugoti</Button>
                    </DialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    );
};

export default InnerSelectingDialog;
